import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { createPortal } from "react-dom";
import { useEditor, EditorContent, ReactNodeViewRenderer, NodeViewWrapper, NodeViewContent } from "@tiptap/react";
import { Mark, Node, mergeAttributes } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Youtube from "@tiptap/extension-youtube";
import UnderlineExtension from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import {
  Bold, Italic, Strikethrough, List, ListOrdered, Quote, Heading2, Heading3,
  Link2 as LinkIcon, Image as ImageIcon, Youtube as YoutubeIcon, Minus, Music,
  Undo2, Redo2, AlignLeft, AlignCenter, AlignRight, Underline,
  Square, CornerUpRight, Type, Images as CarouselIcon, Loader2,
  Film as VideoIcon, Smile, Table as TableIcon,
  Rows3, Columns3, Trash2, Plus, SpellCheck, Palette,
  FolderOpen, Code as EmbedIcon, BarChart3, ArrowLeftRight,
} from "lucide-react";
import EMOJI_GROUPS from "../lib/emojiData";
import { api } from "../lib/api";
import ResizableImageNodeView from "./ResizableImageNodeView";
import ResizableYoutubeNodeView from "./ResizableYoutubeNodeView";
import ResizableDailymotionNodeView from "./ResizableDailymotionNodeView";
import MediaPicker from "./MediaPicker";
import { HtmlEmbed } from "./HtmlEmbedNode";
import { encodeEmbedHtml, decodeEmbedHtml } from "../lib/htmlEmbed";

// Custom Image extension: stores alignment as a class so we can float left/right
// or center the image, and an optional `width` (in percent) so authors can
// resize images to S / M / L / XL or any custom value.
// Left/right float so text wraps beside the image (and continues below
// once the text runs taller than the image) — the simple layout this
// replaced the two-column "image + text" block with. Centre stays a
// plain block (no wrap, nothing beside it makes sense once it's
// centred). Mobile drops the float entirely (see the `@media
// (max-width: 640px)` override in index.css) so the image always
// stacks above the text there instead of being squeezed beside it.
// Kept in sync with the identical mapping in ResizableImageNodeView.jsx
// so the live editor and the saved/published HTML always match.
const ALIGN_CLASS = {
  left: "float-left mr-5 mb-2",
  right: "float-right ml-5 mb-2",
  center: "block mx-auto my-4",
};

// Optional decoration classes — applied on top of the alignment class.
const DECORATION_CLASS = {
  rounded: "rounded-md",
  bordered: "border border-[#0A0F17]",
};

const PRESET_COLORS = [
  { value: "#0A0F17", label: "Default", bgClass: "bg-[#0A0F17]" },
  { value: "#6CABDD", label: "Sky Blue", bgClass: "bg-[#6CABDD]" },
  { value: "#002D75", label: "Navy", bgClass: "bg-[#002D75]" },
  { value: "#E63946", label: "Red", bgClass: "bg-[#E63946]" },
  { value: "#16A34A", label: "Green", bgClass: "bg-[#16A34A]" },
  { value: "#F59E0B", label: "Orange", bgClass: "bg-[#F59E0B]" },
  { value: "#7C3AED", label: "Purple", bgClass: "bg-[#7C3AED]" },
];

const NewBadgeMark = Mark.create({
  name: "newBadge",

  parseHTML() {
    return [
      {
        tag: "span[data-new-badge]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-new-badge": "",
        style: "background-color: #002D75; color: #FFFFFF; padding: 1px 5px; border-radius: 3px; font-size: 10px; font-weight: bold; display: inline-block; line-height: 1.2; vertical-align: middle;",
      }),
      0,
    ];
  },
});

const AlignedImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      alignment: {
        // Freshly-inserted images default to float-left (top-left, text
        // wraps beside it) rather than centre — matches the new simple
        // layout's primary use case. Existing saved posts are unaffected
        // since they already have an explicit `data-alignment` written
        // into their HTML regardless of this default (see `parseHTML`'s
        // own, separate "center" fallback below, kept as-is for any
        // genuinely attribute-less old HTML).
        default: "left",
        parseHTML: (el) =>
          el.getAttribute("data-alignment") || "center",
        renderHTML: (attrs) => ({
          "data-alignment": attrs.alignment,
          class: [
            ALIGN_CLASS[attrs.alignment] || ALIGN_CLASS.center,
            attrs.rounded === false ? "" : DECORATION_CLASS.rounded,
            attrs.bordered ? DECORATION_CLASS.bordered : "",
          ].filter(Boolean).join(" "),
        }),
      },
      width: {
        // Percent of the article column width. `null` = "Auto" (no
        // inline width at all, renders at the column's full natural
        // width) — see the width picker's "Auto" button.
        default: null,
        parseHTML: (el) => {
          const w = el.getAttribute("data-width");
          if (w) return Number(w);
          // Try to back-derive from inline style, e.g. "width: 50%"
          const m = (el.getAttribute("style") || "").match(/width:\s*(\d+)%/i);
          return m ? Number(m[1]) : 50;
        },
        renderHTML: (attrs) => {
          if (!attrs.width) return {};
          return {
            "data-width": String(attrs.width),
            style: `width: ${attrs.width}%; height: auto;`,
          };
        },
      },
      rounded: {
        default: true,
        parseHTML: (el) => (el.getAttribute("data-rounded") || "true") !== "false",
        renderHTML: (attrs) => ({ "data-rounded": String(!!attrs.rounded) }),
      },
      bordered: {
        default: false,
        parseHTML: (el) => (el.getAttribute("data-bordered") || "") === "true",
        renderHTML: (attrs) => ({ "data-bordered": String(!!attrs.bordered) }),
      },
      // Optional URL — when set, the image is wrapped in an <a href> so
      // clicking it (on the public post) navigates to that URL. The href
      // is also stamped on the <img> as `data-href` so it survives round-
      // trips through DOMPurify / TipTap re-parse even if the outer <a>
      // is temporarily lost. `hrefTarget` picks `_blank` vs `_self`.
      href: {
        default: null,
        parseHTML: (el) => {
          const dh = el.getAttribute("data-href");
          if (dh) return dh;
          // Fallback: the img sits inside an <a> in the raw HTML.
          const parent = el.parentElement;
          if (parent && parent.tagName === "A") {
            return parent.getAttribute("href") || null;
          }
          return null;
        },
        renderHTML: (attrs) => (attrs.href ? { "data-href": attrs.href } : {}),
      },
      hrefTarget: {
        default: "_blank",
        parseHTML: (el) => {
          const dht = el.getAttribute("data-href-target");
          if (dht) return dht;
          const parent = el.parentElement;
          if (parent && parent.tagName === "A") {
            return parent.getAttribute("target") || "_blank";
          }
          return "_blank";
        },
        renderHTML: (attrs) =>
          attrs.href && attrs.hrefTarget && attrs.hrefTarget !== "_blank"
            ? { "data-href-target": attrs.hrefTarget }
            : {},
      },
      caption: {
        // Optional credit/attribution text shown directly beneath the
        // image on the public post page. Stored on the <img> as
        // `data-caption` so the saved HTML stays simple — `sanitize.js`
        // wraps the img + caption in a <figure>/<figcaption> at render
        // time. Empty / unset → no caption is shown.
        default: "",
        parseHTML: (el) => el.getAttribute("data-caption") || "",
        renderHTML: (attrs) => {
          const c = (attrs.caption || "").trim();
          return c ? { "data-caption": c } : {};
        },
      },
      // Real intrinsic pixel size, captured once at insert time (see
      // getImageDimensions() below) — NOT the resize percent above.
      // Lets the public post page reserve the correct box via CSS
      // aspect-ratio before the image itself has loaded, fixing a CLS
      // gap (Lighthouse: "Image elements do not have explicit width and
      // height"). Older posts saved before this existed simply have no
      // value here and fall back to the old (unsized) behaviour.
      naturalWidth: {
        default: null,
        parseHTML: (el) => {
          const w = el.getAttribute("data-natural-width");
          return w ? Number(w) : null;
        },
        renderHTML: (attrs) =>
          attrs.naturalWidth ? { "data-natural-width": String(attrs.naturalWidth) } : {},
      },
      naturalHeight: {
        default: null,
        parseHTML: (el) => {
          const h = el.getAttribute("data-natural-height");
          return h ? Number(h) : null;
        },
        renderHTML: (attrs) =>
          attrs.naturalHeight ? { "data-natural-height": String(attrs.naturalHeight) } : {},
      },
    };
  },
  renderHTML({ node, HTMLAttributes }) {
    // Merge all attribute-level renderHTML outputs into a single flat
    // set for the <img> tag. When the node has an `href`, wrap the img
    // in an <a> so the published post is clickable. `data-href` still
    // rides along on the img itself so round-tripping through sanitize
    // + TipTap re-parse doesn't drop the link if the anchor is
    // temporarily stripped.
    const imgAttrs = mergeAttributes(HTMLAttributes);
    const { naturalWidth, naturalHeight } = node.attrs;
    if (naturalWidth && naturalHeight) {
      const ratio = `aspect-ratio: ${naturalWidth} / ${naturalHeight};`;
      imgAttrs.style = imgAttrs.style ? `${imgAttrs.style} ${ratio}` : ratio;
    }
    const href = node.attrs.href;
    if (href) {
      const target = node.attrs.hrefTarget || "_blank";
      const anchorAttrs = { href, class: "image-link" };
      anchorAttrs.target = target;
      if (target === "_blank") anchorAttrs.rel = "noreferrer noopener";
      return ["a", anchorAttrs, ["img", imgAttrs]];
    }
    return ["img", imgAttrs];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNodeView);
  },
});

// Reads an image's real pixel dimensions once it's loaded, so newly
// inserted images can carry naturalWidth/naturalHeight (see AlignedImage
// above) — used by both the upload and Media-Library insert paths.
const getImageDimensions = (url) =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = url;
  });

const ResizableYoutube = Youtube.extend({  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: 100,
        parseHTML: (element) => {
          const val = element.getAttribute("data-width");
          return val ? parseInt(val, 10) : 100;
        },
        renderHTML: (attributes) => {
          return { "data-width": attributes.width || 100 };
        },
      },
      alignment: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-alignment") || "center",
        renderHTML: (attributes) => {
          return { "data-alignment": attributes.alignment || "center" };
        },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const pct = HTMLAttributes["data-width"] || 100;
    const align = HTMLAttributes["data-alignment"] || "center";
    let alignStyle = "display: block;";
    if (align === "center") alignStyle += "margin-left: auto; margin-right: auto; clear: both;";
    else if (align === "left") alignStyle += "float: left; margin-right: 20px; margin-bottom: 8px;";
    else if (align === "right") alignStyle += "float: right; margin-left: 20px; margin-bottom: 8px;";

    let embedSrc = HTMLAttributes.src || "";
    // Robust watch-to-embed URL resolution
    if (embedSrc && !embedSrc.includes("/embed/")) {
      const match = embedSrc.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube-nocookie\.com\/embed\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/i);
      if (match) {
        embedSrc = `https://www.youtube-nocookie.com/embed/${match[1]}`;
      }
    }

    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-youtube-video": "",
        class: "tiptap-youtube rounded-md",
        style: `width: ${pct}%; aspect-ratio: 16 / 9; max-width: 100%; ${alignStyle}`,
      }),
      ["iframe", {
        src: embedSrc,
        title: "YouTube video player",
        frameborder: "0",
        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
        allowfullscreen: "true",
        class: "w-full h-full block rounded-md"
      }]
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableYoutubeNodeView);
  },
});

// Dailymotion video embed. TipTap ships no official Dailymotion package, so
// this is a standalone custom node mirroring ResizableYoutube's shape (same
// width/alignment attrs, same resizable node view pattern) rather than an
// `.extend()` of anything. Handles canonical video URLs, dai.ly short links
// and already-resolved embed URLs.
const DAILYMOTION_URL_RE = /(?:dailymotion\.com\/(?:video|embed\/video|hub)\/|dai\.ly\/)([a-zA-Z0-9]+)/i;

const isValidDailymotionUrl = (url) => DAILYMOTION_URL_RE.test(url || "");

const ResizableDailymotion = Node.create({
  name: "dailymotion",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      width: {
        default: 100,
        parseHTML: (element) => {
          const val = element.getAttribute("data-width");
          return val ? parseInt(val, 10) : 100;
        },
        renderHTML: (attributes) => {
          return { "data-width": attributes.width || 100 };
        },
      },
      alignment: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-alignment") || "center",
        renderHTML: (attributes) => {
          return { "data-alignment": attributes.alignment || "center" };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-dailymotion-video]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const pct = HTMLAttributes["data-width"] || 100;
    const align = HTMLAttributes["data-alignment"] || "center";
    let alignStyle = "display: block;";
    if (align === "center") alignStyle += "margin-left: auto; margin-right: auto; clear: both;";
    else if (align === "left") alignStyle += "float: left; margin-right: 20px; margin-bottom: 8px;";
    else if (align === "right") alignStyle += "float: right; margin-left: 20px; margin-bottom: 8px;";

    let embedSrc = HTMLAttributes.src || "";
    // Robust watch/short-link-to-embed URL resolution
    if (embedSrc && !embedSrc.includes("/embed/")) {
      const match = embedSrc.match(DAILYMOTION_URL_RE);
      if (match) {
        embedSrc = `https://www.dailymotion.com/embed/video/${match[1]}`;
      }
    }

    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-dailymotion-video": "",
        class: "tiptap-dailymotion rounded-md",
        style: `width: ${pct}%; aspect-ratio: 16 / 9; max-width: 100%; ${alignStyle}`,
      }),
      ["iframe", {
        src: embedSrc,
        title: "Dailymotion video player",
        frameborder: "0",
        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen",
        allowfullscreen: "true",
        class: "w-full h-full block rounded-md"
      }]
    ];
  },

  addCommands() {
    return {
      setDailymotionVideo: (options) => ({ commands }) => {
        if (!isValidDailymotionUrl(options.src)) {
          return false;
        }
        return commands.insertContent({ type: this.name, attrs: options });
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableDailymotionNodeView);
  },
});

// Float-left/float-right replacement: a two-column grid with the image in
// one column and real, editable rich content (heading + paragraph(s), via
// `content: "block+"`) in the other — collapses to one column on mobile
// with the image always first in document order, so it always stacks
// above the text (no float-gap issues). `side` controls which desktop
// column the image sits in via CSS `order` — see `.tiptap-image-text` in
// index.css, shared with the published-post render in
// RichBodyWithCarousels.jsx so editor and published output match exactly.
const IMAGE_TEXT_HANDLE_BASE =
  "absolute h-3 w-3 rounded-sm bg-white border-2 border-[#0A0F17] shadow ring-1 ring-black/10 z-10";

// Lets the main toolbar (rendered outside this node's own React subtree)
// show/drive the Size/Align/Style row for whichever image+text block's
// image is currently "active" (clicked into). One Provider per
// `RichTextEditor` instance (body / custom intro each get their own),
// so the two fields never cross-talk. `active` is either null or
// `{ id, attrs, updateAttributes }` for the single currently-active
// block — only one block can be active at a time (see the hand-off
// logic in the NodeView below), so the toolbar always has one
// unambiguous target.
const ImageTextToolbarContext = createContext(null);

function ImageTextBlockNodeView({ node, updateAttributes, selected }) {
  const { src, alt, side, rounded, bordered, width, align } = node.attrs;
  const mediaRef = useRef(null);
  const [mediaActive, setMediaActive] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [livePercent, setLivePercent] = useState(null);
  const instanceId = useId();
  const toolbarCtx = useContext(ImageTextToolbarContext);

  // Click anywhere outside this block's media area deselects it, so the
  // resize handles don't linger once the admin moves on to something else.
  // Clicks inside the main toolbar's Size/Align/Style row (guarded by
  // `data-image-text-toolbar-guard`) are explicitly excluded — otherwise
  // the mousedown on e.g. the "M" button would fire this listener first
  // and deactivate (hiding the row) before the click itself registers.
  useEffect(() => {
    if (!mediaActive) return undefined;
    const onDocMouseDown = (e) => {
      if (e.target?.closest?.("[data-image-text-toolbar-guard]")) return;
      if (mediaRef.current && !mediaRef.current.contains(e.target)) {
        setMediaActive(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [mediaActive]);

  // Register/update this block as the toolbar's active image+text target
  // whenever it's active, keeping `attrs` in sync so Size/Align button
  // highlighting reflects live drag/align changes instantly.
  useEffect(() => {
    if (!toolbarCtx || !mediaActive) return undefined;
    toolbarCtx.setActive({ id: instanceId, attrs: node.attrs, updateAttributes, deactivate: () => setMediaActive(false) });
    return undefined;
  }, [mediaActive, node.attrs, updateAttributes, toolbarCtx, instanceId]);

  // Clear this block from the toolbar context when it deactivates or
  // unmounts — but only if it's still the one the toolbar is pointing
  // at (a different block may have already taken over, see hand-off
  // below, in which case leave that one in place).
  useEffect(() => {
    if (!toolbarCtx || !mediaActive) return undefined;
    return () => {
      toolbarCtx.setActive((cur) => (cur && cur.id === instanceId ? null : cur));
    };
  }, [mediaActive, toolbarCtx, instanceId]);

  const visiblePercent = livePercent ?? width;
  const imgClass = `block h-auto ${visiblePercent ? "" : "w-full"} ${rounded !== false ? "rounded-md" : ""} ${bordered ? "border-2 border-[#0A0F17]" : ""}`;
  const imgStyle = visiblePercent ? { width: `${visiblePercent}%` } : undefined;

  const startDrag = (corner) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const mediaEl = mediaRef.current;
    const mediaRect = mediaEl?.getBoundingClientRect();
    const mediaWidth = mediaRect?.width || 200;
    const startX = e.clientX;
    const startWidth = (width ? (width / 100) * mediaWidth : mediaWidth);
    const dir = corner === "tr" || corner === "br" ? 1 : -1;

    setDragging(true);
    const iframes = document.querySelectorAll("iframe");
    iframes.forEach((ifr) => (ifr.style.pointerEvents = "none"));

    const onMove = (ev) => {
      const delta = (ev.clientX - startX) * dir;
      const newPx = Math.max(30, startWidth + delta);
      const pct = Math.min(100, Math.max(10, Math.round((newPx / mediaWidth) * 100)));
      setLivePercent(pct);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const ifrs = document.querySelectorAll("iframe");
      ifrs.forEach((ifr) => (ifr.style.pointerEvents = ""));
      setDragging(false);
      setLivePercent((finalPct) => {
        if (finalPct != null) updateAttributes({ width: finalPct });
        return null;
      });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <NodeViewWrapper
      className={`tiptap-image-text relative my-6 ${selected ? "outline outline-2 outline-[#6CABDD] outline-offset-2" : ""}`}
      data-side={side}
      data-testid="editor-image-text-block"
    >
      {selected ? (
        <button
          type="button"
          contentEditable={false}
          data-testid="image-text-flip-side"
          onClick={() => updateAttributes({ side: side === "left" ? "right" : "left" })}
          className="absolute -top-3 right-2 z-10 inline-flex items-center gap-1 rounded-md border border-[#0A0F17] bg-white px-2 py-1 font-display text-[11px] font-bold shadow-sm hover:bg-[#F8FAFC]"
          title="Swap which side the image sits on"
        >
          <ArrowLeftRight size={12} /> Image {side}
        </button>
      ) : null}
      <div
        ref={mediaRef}
        className={`tiptap-image-text-media relative ${mediaActive ? "outline outline-2 outline-brand-accent outline-offset-2" : ""}`}
        contentEditable={false}
        data-testid="image-text-media"
        data-align={align || "left"}
        onMouseDown={(e) => {
          // Don't let this bubble to TipTap's own click handling — we
          // manage "is this image active for resizing" purely as local
          // state, since the image here is an attribute of this block,
          // not its own selectable node.
          e.stopPropagation();
          // Hand off from whichever block was previously active (if any,
          // and if it isn't this one) so only one block ever shows
          // resize handles / drives the toolbar at a time.
          if (toolbarCtx?.active && toolbarCtx.active.id !== instanceId) {
            toolbarCtx.active.deactivate?.();
          }
          setMediaActive(true);
        }}
      >
        {src ? (
          <img src={src} alt={alt || ""} className={imgClass} style={imgStyle} draggable={false} />
        ) : (
          <div className="flex h-40 w-full items-center justify-center rounded-md border border-dashed border-black/20 text-xs text-black/40">
            No image
          </div>
        )}

        {dragging && livePercent != null ? (
          <span
            className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 rounded-sm bg-[#0A0F17] px-2 py-0.5 font-display text-xs font-bold text-white"
            data-testid="image-text-resize-badge"
          >
            {livePercent}%
          </span>
        ) : null}

        {mediaActive ? (
          <>
            <span data-testid="image-text-handle-tl" onMouseDown={startDrag("tl")} className={`${IMAGE_TEXT_HANDLE_BASE} -left-1.5 -top-1.5 cursor-nwse-resize`} />
            <span data-testid="image-text-handle-tr" onMouseDown={startDrag("tr")} className={`${IMAGE_TEXT_HANDLE_BASE} -right-1.5 -top-1.5 cursor-nesw-resize`} />
            <span data-testid="image-text-handle-bl" onMouseDown={startDrag("bl")} className={`${IMAGE_TEXT_HANDLE_BASE} -bottom-1.5 -left-1.5 cursor-nesw-resize`} />
            <span data-testid="image-text-handle-br" onMouseDown={startDrag("br")} className={`${IMAGE_TEXT_HANDLE_BASE} -bottom-1.5 -right-1.5 cursor-nwse-resize`} />
          </>
        ) : null}
      </div>
      <NodeViewContent className="tiptap-image-text-content" data-testid="image-text-content" />
    </NodeViewWrapper>
  );
}

const ImageTextBlock = Node.create({
  name: "imageTextBlock",
  group: "block",
  content: "block+",
  draggable: true,

  addAttributes() {
    return {
      // No renderHTML/parseHTML here on purpose — these aren't rendered
      // as literal HTML attributes anywhere (that would stamp an
      // invalid src="..."/alt="..." onto the outer wrapper <div>, which
      // is what the old code did by accident). The real <img src>/<alt>
      // are built manually inside renderHTML()/getAttrs() below instead.
      src: { default: null, renderHTML: () => ({}), parseHTML: () => null },
      alt: { default: "", renderHTML: () => ({}), parseHTML: () => null },
      side: {
        default: "left",
        parseHTML: (el) => el.getAttribute("data-side") || "left",
        renderHTML: (attrs) => ({ "data-side": attrs.side || "left" }),
      },
      rounded: {
        default: true,
        parseHTML: (el) => el.getAttribute("data-rounded") !== "false",
        renderHTML: (attrs) => ({ "data-rounded": attrs.rounded !== false ? "true" : "false" }),
      },
      bordered: {
        default: false,
        parseHTML: (el) => el.getAttribute("data-bordered") === "true",
        renderHTML: (attrs) => ({ "data-bordered": attrs.bordered ? "true" : "false" }),
      },
      width: {
        default: null,
        parseHTML: (el) => {
          const v = el.getAttribute("data-width");
          return v ? parseInt(v, 10) : null;
        },
        renderHTML: (attrs) => (attrs.width ? { "data-width": String(attrs.width) } : {}),
      },
      // Where the image sits *within its own grid cell* (independent of
      // `side`, which picks which cell/column it's in). Read/written on
      // `.tiptap-image-text-media` (not the outer div) since that's the
      // flex container `data-align` actually styles in index.css — so,
      // like src/alt above, no attribute-level renderHTML (would stamp
      // it on the wrong element); handled manually in renderHTML() and
      // parseHTML() below instead.
      align: {
        default: "left",
        parseHTML: (el) => el.querySelector(".tiptap-image-text-media")?.getAttribute("data-align") || "left",
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div.tiptap-image-text",
        contentElement: (el) => el.querySelector(".tiptap-image-text-content"),
        getAttrs: (el) => {
          const img = el.querySelector(".tiptap-image-text-media img");
          return { src: img?.getAttribute("src") || null, alt: img?.getAttribute("alt") || "" };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    const rounded = node.attrs.rounded !== false;
    const bordered = !!node.attrs.bordered;
    const width = node.attrs.width;
    const align = node.attrs.align || "left";
    const imgClass = `block h-auto ${width ? "" : "w-full"} ${rounded ? "rounded-md" : ""} ${bordered ? "border-2 border-[#0A0F17]" : ""}`.trim();
    const imgAttrs = { src: node.attrs.src, alt: node.attrs.alt || "", class: imgClass };
    if (width) imgAttrs.style = `width:${width}%`;
    return [
      "div",
      mergeAttributes(HTMLAttributes, { class: "tiptap-image-text" }),
      ["div", { class: "tiptap-image-text-media", "data-align": align }, ["img", imgAttrs]],
      ["div", { class: "tiptap-image-text-content" }, 0],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageTextBlockNodeView);
  },
});

function PollNodeView({ node }) {
  return (
    <NodeViewWrapper className="rounded-md border border-dashed border-black/20 p-5 bg-[#F8FAFC] text-center my-4 font-display text-sm font-bold text-[var(--brand-body)] select-none">
      <span className="text-[#6CABDD] mr-1 text-base">📊</span> Embedded Fan Poll (ID: <span className="font-mono font-bold text-[var(--brand-ink)]">{node.attrs.id}</span>)
    </NodeViewWrapper>
  );
}

const PollNode = Node.create({
  name: "poll",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      id: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-poll-id") || "",
        renderHTML: (attrs) => (attrs.id ? { "data-poll-id": attrs.id } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.poll-placeholder[data-poll-id]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        class: "poll-placeholder",
        contenteditable: "false",
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PollNodeView);
  },
});


// ---- Text-size mark ------------------------------------------------------ //
//
// Wraps the selected text in a <span style="font-size: Npt"> so authors can
// pick a size in points (matches the mental model from Word / Google Docs).
// Stored as a plain inline style so DOMPurify keeps it on the published
// page via the existing `style` allowlist entry.
//
// Sizes were picked to match the typographic ladder used elsewhere on the
// site (14 = base body copy, 18 = subhead, 24 = section title, 32 = hero).
const TEXT_SIZE_OPTIONS = [
  { value: "10", label: "10pt — fine print" },
  { value: "12", label: "12pt — small" },
  { value: "14", label: "14pt — body (default)" },
  { value: "16", label: "16pt — medium" },
  { value: "18", label: "18pt — lead" },
  { value: "20", label: "20pt — pull-quote" },
  { value: "24", label: "24pt — sub-heading" },
  { value: "32", label: "32pt — hero" },
];

// Compact toolbar version — size presets covering common copy needs.
// `value === ""` clears any size mark and reverts to body copy.
const TEXT_SIZE_BUTTONS = [
  { value: "",   label: "Aa", title: "Default body text" },
  { value: "6",  label: "6",  title: "6pt — micro" },
  { value: "8",  label: "8",  title: "8pt — caption" },
  { value: "10", label: "10", title: "10pt — fine print" },
  { value: "12", label: "12", title: "12pt — small" },
  { value: "16", label: "16", title: "16pt — medium" },
  { value: "20", label: "20", title: "20pt — large" },
  { value: "28", label: "28", title: "28pt — extra large" },
];

// ---- Video node ---------------------------------------------------------- //
//
// Self-hosted video embed. The toolbar uploads an MP4/WebM/MOV to
// /api/uploads and inserts a <video controls playsinline preload="metadata">
// at the cursor. Renders as a responsive 16:9 box so the player matches the
// YouTube embed visually. Sanitize allowlist lets <video> survive on the
// public page (see /app/frontend/src/lib/sanitize.js).
const Video = Node.create({
  name: "video",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      poster: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: "video[src]",
        getAttrs: (el) => ({
          src: el.getAttribute("src"),
          poster: el.getAttribute("poster"),
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "video",
      mergeAttributes(HTMLAttributes, {
        controls: "true",
        playsinline: "true",
        preload: "metadata",
        class: "tiptap-video w-full aspect-video my-6 rounded-md bg-black",
      }),
    ];
  },

  addCommands() {
    return {
      setVideo:
        (options) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: options }),
    };
  },
});


const TextSize = Mark.create({
  name: "textSize",
  inclusive: true,

  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: (el) => {
          const explicit = el.getAttribute("data-text-size");
          if (explicit) return explicit;
          // Back-derive from inline style for content saved by older versions
          // (or pasted from another doc) so the dropdown reflects reality.
          const m = (el.getAttribute("style") || "").match(/font-size:\s*(\d+)pt/i);
          return m ? m[1] : null;
        },
        renderHTML: (attrs) => {
          if (!attrs.size) return {};
          return {
            "data-text-size": String(attrs.size),
            style: `font-size: ${attrs.size}pt;`,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      { tag: "span[data-text-size]" },
      { tag: "span[style*='font-size']" },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setTextSize:
        (size) =>
        ({ chain }) =>
          chain().setMark("textSize", { size }).run(),
      unsetTextSize:
        () =>
        ({ chain }) => chain().unsetMark("textSize").run(),
    };
  },
});


export default function RichTextEditor({ value, onChange, placeholder, minHeight = 420, editorRef }) {
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null); // holds the clicked element!
  const [pollDialog, setPollDialog] = useState(null); // null or { selected_id: "" }
  const [allPolls, setAllPolls] = useState([]);
  const [, setSelectionTick] = useState(0);
  // Tracks which image+text block's image (if any) is currently active
  // for this editor instance, so the main toolbar can show its own
  // Size/Align/Style row — see `ImageTextToolbarContext` above.
  const [imageTextActive, setImageTextActive] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // Newer versions of @tiptap/starter-kit bundle a Link extension
        // by default. We register our own customised Link below, so
        // disable the bundled one to silence the "Duplicate extension
        // names found: [link]" console warning on every editor mount.
        link: false,
      }),
      // Link mark extended to persist per-link `target` + `rel` so admins
      // can choose, on a link-by-link basis, whether the link opens in a
      // new tab. Defaults stay backwards-compatible (existing links with
      // no explicit `target` keep opening in a new tab).
      Link.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            target: {
              default: "_blank",
              parseHTML: (el) => el.getAttribute("target") || "_blank",
              renderHTML: (attrs) => (attrs.target ? { target: attrs.target } : {}),
            },
            rel: {
              default: "noreferrer noopener",
              parseHTML: (el) => el.getAttribute("rel") || "noreferrer noopener",
              renderHTML: (attrs) => (attrs.rel ? { rel: attrs.rel } : {}),
            },
          };
        },
      }).configure({
        openOnClick: false,
        autolink: true,
        protocols: ["http", "https", "mailto"],
        HTMLAttributes: {
          class: "text-[#6CABDD] underline underline-offset-2 hover:text-[var(--brand-ink)]",
        },
      }),
      AlignedImage.configure({
        inline: false,
        allowBase64: false,
      }),
      ResizableYoutube.configure({
        // Wrap the iframe in a responsive 16:9 container; nocookie + no playlist
        // hijacking so we stay clean of YouTube's tracking cookies on first load.
        nocookie: true,
        controls: true,
        HTMLAttributes: {
          class: "tiptap-youtube w-full aspect-video my-6 rounded-md",
        },
      }),
      ResizableDailymotion,
      ImageTextBlock,
      Video,
      // HTML embed atom — lets admins paste Facebook/Twitter/Instagram/
      // TikTok/custom iframe HTML into the post. See lib/htmlEmbed.js
      // for the sanitise + hydrate pipeline that runs on the public
      // post page.
      HtmlEmbed,
      TextSize,
      UnderlineExtension,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      PollNode,
      // Table support — basic resizable tables with sortable header row,
      // explicit table/tr/th/td tags so HTML round-trips cleanly when
      // posts are re-loaded for editing. Wrapper class is what we style
      // in CSS to get the alternating-row look + horizontal scroll on
      // narrow screens.
      Table.configure({
        resizable: true,
        HTMLAttributes: { class: "tiptap-table" },
      }),
      TableRow,
      TableHeader,
      TableCell,
      // Text colour — TextStyle is the carrier mark for inline `style="…"`,
      // Color attaches a setColor() command that writes the colour into it.
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      NewBadgeMark,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "tiptap-prose prose prose-slate max-w-none px-4 py-4 focus:outline-none",
        style: `min-height: ${minHeight}px;`,
        // Native browser spell-check on by default. The toolbar toggle
        // flips this live via setAttribute on the editor DOM. The lang
        // attribute is a hint so Chrome/Edge pick the correct dictionary
        // for the wavy-underline (British vs American spellings differ).
        spellcheck: "true",
        lang: "en-GB",
      },
      // Clean up rubbish from Google Docs / MS Word / Notion paste so the
      // basic semantic tags (p, h2/h3, b/strong, i/em, ul/ol, a) survive
      // and everything else (inline fonts, Mso classes, comments) is
      // stripped before TipTap parses the HTML.
      transformPastedHTML: (html) => {
        if (!html) return html;
        return (
          html
            // MS Word / Outlook
            .replace(/<!--[\s\S]*?-->/g, "")
            .replace(/<\/?(o:p|w:[a-z]+|m:[a-z]+|v:[a-z]+)[^>]*>/gi, "")
            .replace(/<\/?(meta|link|style|script|font)[^>]*>/gi, "")
            // Class attributes referencing Office / Docs internals
            .replace(/\s(class|style|lang|xml:lang|align)="[^"]*"/gi, "")
            // Font tags converted to span (tiptap will drop unknown spans)
            .replace(/<\/?span[^>]*>/gi, "")
            // Strip empty paragraphs from Word's "MsoNormal" leftovers
            .replace(/<p>\s*(&nbsp;|\u00a0|\s)*\s*<\/p>/gi, "")
            // Convert <div> to <p> so TipTap treats them as block paragraphs
            .replace(/<div([^>]*)>/gi, "<p$1>")
            .replace(/<\/div>/gi, "</p>")
        );
      },
    },
    onUpdate: ({ editor }) => {
      // TipTap may produce '<p></p>' or '<p><br></p>' for an empty editor;
      // editor.isEmpty handles both cases reliably.
      onChange(editor.isEmpty ? "" : editor.getHTML());
    },
    // `isImageSelected` below (and every other `editor.isActive(...)` toolbar
    // check) is only recomputed when THIS component re-renders. Selecting a
    // node (clicking an image, or `selectInsertedImage()` right after
    // insert/upload) is a selection-only transaction — it does NOT fire
    // `onUpdate` (no document content changed) — so without this handler
    // the image toolbar stayed stuck hidden until some unrelated edit (e.g.
    // dragging a resize handle, which DOES mutate the `width` attribute)
    // happened to trigger a re-render and reveal it. `selectionTick` just
    // forces that re-render on every selection change too.
    onSelectionUpdate: () => setSelectionTick((t) => t + 1),
  });

  // If the parent passes in a different `value` after the editor mounts
  // (e.g. async fetch in edit mode), sync it in — but only when the user
  // isn't actively editing, to avoid clobbering their cursor.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (current === next || (next === "" && editor.isEmpty)) return;
    if (editor.isFocused) return;
    editor.commands.setContent(next, false);
  }, [editor, value]);

  useEffect(() => {
    if (editor && editorRef) {
      editorRef.current = editor;
    }
    return () => {
      if (editorRef) {
        editorRef.current = null;
      }
    };
  }, [editor, editorRef]);

  const [linkDialog, setLinkDialog] = useState(null);
  const [tableDialog, setTableDialog] = useState(null);
  const [carouselPicker, setCarouselPicker] = useState(null); // null = closed, {} = open
  // HTML-embed dialog state: null = closed; {html: string, pos?: number} =
  // open. `pos` is set when we're editing an existing embed node (so on
  // Save we can either replace-at-pos or insert-at-selection).
  const [embedDialog, setEmbedDialog] = useState(null);
  const [spotifyDialog, setSpotifyDialog] = useState(null); // null | { url: "" }
  const [imageUploading, setImageUploading] = useState(false);
  // Native browser spell-check toggle. On by default. Persisted in localStorage
  // so a logged-in admin's preference survives page reloads.
  const [spellCheck, setSpellCheck] = useState(() => {
    try {
      const v = window.localStorage.getItem("last:editor:spellcheck");
      return v === null ? true : v === "true";
    } catch (_) {
      return true;
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem("last:editor:spellcheck", spellCheck ? "true" : "false"); } catch (_) { /* ignore */ }
  }, [spellCheck]);
  // Flip the contenteditable attribute on the editor DOM so Chrome/Safari
  // pick up the new setting immediately (without losing focus or content).
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view?.dom;
    if (!dom) return;
    dom.setAttribute("spellcheck", spellCheck ? "true" : "false");
  }, [editor, spellCheck]);

  // Carousel shortcode insertion. Opens a dialog listing every admin
  // carousel; clicking one drops `[[carousel:slug]]` on its own line at
  // the cursor so the rich-body renderer picks it up.
  const openCarouselPicker = useCallback(() => {
    if (!editor) return;
    setCarouselPicker({ items: null, error: "" });
    api
      .get("/admin/carousels")
      .then((r) => setCarouselPicker({ items: r.data || [], error: "" }))
      .catch(() =>
        setCarouselPicker({
          items: [],
          error: "Couldn't load your carousels — try saving and reopening.",
        }),
      );
  }, [editor]);

  const insertCarousel = (slug) => {
    if (!editor || !slug) return;
    // Drop the shortcode in its own paragraph so the renderer can split
    // cleanly without leaving stray inline text on either side.
    editor
      .chain()
      .focus()
      .insertContent([
        { type: "paragraph", content: [{ type: "text", text: `[[carousel:${slug}]]` }] },
        { type: "paragraph" },
      ])
      .run();
    setCarouselPicker(null);
  };

  // Open the HTML embed dialog. Called from the toolbar button (fresh
  // insert) and from the `html-embed-edit` DOM event the node view
  // dispatches when the admin clicks "Edit" on an existing embed.
  const openEmbedDialog = useCallback(
    ({ html = "", pos = null, width = "full" } = {}) => {
      setEmbedDialog({ html, pos, width });
    },
    []
  );

  const openPollDialog = async () => {
    try {
      const r = await api.get("/polls");
      setAllPolls(r.data || []);
      setPollDialog({ selected_id: r.data?.[0]?.id || "" });
    } catch (_) {
      alert("Failed to load polls. Please make sure some polls exist in your database.");
    }
  };

  useEffect(() => {
    if (!editor) return undefined;
    const dom = editor.view.dom;
    const onEdit = (e) => {
      const detail = e.detail || {};
      openEmbedDialog({
        html: decodeEmbedHtml(detail.htmlB64 || ""),
        pos: typeof detail.pos === "number" ? detail.pos : null,
        width: detail.width || "full",
      });
    };
    dom.addEventListener("html-embed-edit", onEdit);
    return () => dom.removeEventListener("html-embed-edit", onEdit);
  }, [editor, openEmbedDialog]);

  const parseSpotifyUrl = (urlStr) => {
    const url = (urlStr || "").trim();
    const match = url.match(/open\.spotify\.com\/(playlist|track|album|show|episode)\/([a-zA-Z0-9]+)/i);
    if (match) {
      return {
        type: match[1],
        id: match[2],
      };
    }
    return null;
  };

  const expandUrlToEmbedHtml = (inputUrl) => {
    const url = inputUrl.trim();
    if (!/^https?:\/\/[^\s]+$/i.test(url)) {
      return url;
    }
    const spotify = parseSpotifyUrl(url);
    if (spotify) {
      const height = spotify.type === "track" ? "152" : "380";
      return `<iframe src="https://open.spotify.com/embed/${spotify.type}/${spotify.id}?utm_source=generator&theme=0" width="100%" height="${height}" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" style="border-radius:12px;"></iframe>`;
    }
    if (/instagram\.com\/(?:p|reel|tv)\/([a-z0-9_-]+)/i.test(url)) {
      return `<blockquote class="instagram-media" data-instgrm-permalink="${url}" data-instgrm-version="14" style="background:#FFF; border:0; border-radius:3px; box-shadow:0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15); margin: 1px; max-width:540px; min-width:326px; padding:0; width:99.375%; width:-webkit-calc(100% - 2px); width:calc(100% - 2px);"><a href="${url}"></a></blockquote><script async src="//www.instagram.com/embed.js"></script>`;
    }
    if (/twitter\.com\/[a-z0-9_]+\/status\/(\d+)/i.test(url) || /x\.com\/[a-z0-9_]+\/status\/(\d+)/i.test(url)) {
      return `<blockquote class="twitter-tweet"><a href="${url}"></a></blockquote><script async src="https://platform.twitter.com/widgets.js" charset="utf-8"></script>`;
    }
    const tiktokMatch = url.match(/tiktok\.com\/@[a-z0-9_.-]+\/video\/(\d+)/i);
    if (tiktokMatch) {
      const videoId = tiktokMatch[1];
      return `<blockquote class="tiktok-embed" data-video-id="${videoId}" style="max-width: 605px;min-width: 325px;"><section><a target="_blank" title="${url}" href="${url}"></a></section></blockquote><script async src="https://www.tiktok.com/embed.js"></script>`;
    }
    let ytId = null;
    const ytMatch1 = url.match(/youtube\.com\/watch\?v=([a-z0-9_-]+)/i);
    if (ytMatch1) ytId = ytMatch1[1];
    const ytMatch2 = url.match(/youtu\.be\/([a-z0-9_-]+)/i);
    if (ytMatch2) ytId = ytMatch2[1];
    const ytMatch3 = url.match(/youtube\.com\/embed\/([a-z0-9_-]+)/i);
    if (ytMatch3) ytId = ytMatch3[1];
    if (ytId) {
      return `<iframe src="https://www.youtube.com/embed/${ytId}" width="100%" height="400" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
    }
    return url;
  };

  const applySpotifyEmbed = () => {
    if (!editor || !spotifyDialog) return;
    const raw = (spotifyDialog.url || "").trim();
    if (!raw) {
      setSpotifyDialog(null);
      return;
    }
    const parsed = parseSpotifyUrl(raw);
    if (!parsed) {
      alert("Invalid Spotify URL. Please enter a valid playlist, album, or track link.");
      return;
    }
    const embedUrl = `https://open.spotify.com/embed/${parsed.type}/${parsed.id}?utm_source=generator&theme=0`;
    const height = parsed.type === "track" ? "152" : "380";
    const iframeHtml = `<iframe src="${embedUrl}" width="100%" height="${height}" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" style="border-radius:12px; border:0;"></iframe>`;
    
    const htmlB64 = encodeEmbedHtml(iframeHtml);
    const nodeSpec = { type: "htmlEmbed", attrs: { htmlB64, width: "full" } };
    editor.chain().focus().insertContent(nodeSpec).run();
    setSpotifyDialog(null);
  };

  const applyEmbed = () => {
    if (!editor || !embedDialog) return;
    let raw = (embedDialog.html || "").trim();
    if (!raw) {
      // Empty save on a new insert: just close. On edit-existing:
      // delete the node.
      if (embedDialog.pos !== null) {
        editor
          .chain()
          .focus()
          .setNodeSelection(embedDialog.pos)
          .deleteSelection()
          .run();
      }
      setEmbedDialog(null);
      return;
    }
    // Auto-expand URL to HTML Embed snippet
    raw = expandUrlToEmbedHtml(raw);

    const htmlB64 = encodeEmbedHtml(raw);
    const width = embedDialog.width || "full";
    const nodeSpec = { type: "htmlEmbed", attrs: { htmlB64, width } };
    const chain = editor.chain().focus();
    if (embedDialog.pos !== null) {
      chain
        .setNodeSelection(embedDialog.pos)
        .deleteSelection()
        .insertContent(nodeSpec)
        .run();
    } else {
      chain.insertContent(nodeSpec).run();
    }
    setEmbedDialog(null);
  };

  // Opens a tiny modal that lets the admin set the URL and pick whether
  // the link should open in a new tab. Replaces the old `window.prompt`
  // flow, which couldn't ask about target at the same time.
  const setLink = useCallback(() => {
    if (!editor) return;
    // If an image is selected, edit the image's link URL instead of the
    // text-link mark (which cannot wrap a block node). This is what
    // makes "click to link an image" actually work — the classic Link
    // extension in TipTap is a Mark and only applies to inline text, so
    // hitting the same button on an image previously did nothing.
    if (editor.isActive("image")) {
      const imgAttrs = editor.getAttributes("image");
      setLinkDialog({
        mode: "image",
        url: imgAttrs.href || "",
        newTab: (imgAttrs.hrefTarget || "_blank") === "_blank",
      });
      return;
    }
    const attrs = editor.getAttributes("link");
    setLinkDialog({
      mode: "text",
      url: attrs.href || "",
      newTab: (attrs.target || "_blank") === "_blank",
    });
  }, [editor]);

  const applyLink = () => {
    if (!editor || !linkDialog) return;
    const url = (linkDialog.url || "").trim();
    // Image-link mode: writes href + hrefTarget onto the selected image
    // node. Empty URL clears the link.
    if (linkDialog.mode === "image") {
      editor
        .chain()
        .focus()
        .updateAttributes("image", {
          href: url || null,
          hrefTarget: linkDialog.newTab ? "_blank" : "_self",
        })
        .run();
      setLinkDialog(null);
      return;
    }
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      setLinkDialog(null);
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({
        href: url,
        target: linkDialog.newTab ? "_blank" : "_self",
        rel: linkDialog.newTab ? "noreferrer noopener" : null,
      })
      .run();
    setLinkDialog(null);
  };

  const setImageAlign = (alignment) => {
    if (!editor) return;
    const attrs = editor.getAttributes("image");
    // Left/right only ever look different from a full-width image once
    // it's narrower than the editor — so give it a sensible default
    // width the first time it's moved off-centre, instead of silently
    // doing nothing at "Auto" width. Centre and already-sized images are
    // left exactly as they are (this never changes an existing width).
    const needsDefaultWidth = alignment !== "center" && !attrs?.width;
    editor
      .chain()
      .focus()
      .updateAttributes("image", {
        alignment,
        ...(needsDefaultWidth ? { width: 50 } : {}),
      })
      .run();
  };

  const setImageWidth = (width) => {
    if (!editor) return;
    // width === null clears the inline width and lets the alignment class take over.
    editor.chain().focus().updateAttributes("image", { width }).run();
  };

  const toggleImageBorder = () => {
    if (!editor) return;
    const current = !!editor.getAttributes("image")?.bordered;
    editor.chain().focus().updateAttributes("image", { bordered: !current }).run();
  };

  const toggleImageRounded = () => {
    if (!editor) return;
    // Default is rounded: true. We track the inverse so the button reads "Round"
    // and goes active when corners are actually rounded.
    const current = editor.getAttributes("image")?.rounded !== false;
    editor.chain().focus().updateAttributes("image", { rounded: !current }).run();
  };

  const setTextSize = (size) => {
    if (!editor) return;
    // The select stole focus when it opened — refocus the editor so the
    // current selection is restored before we apply the mark. Without this
    // the mark has no range to write to and clicking the dropdown does
    // nothing visible (this was the v32 bug).
    editor.commands.focus();
    if (!size) {
      editor.chain().focus().unsetMark("textSize").run();
      return;
    }
    editor.chain().focus().setMark("textSize", { size }).run();
  };

  const onPickImage = () => fileInputRef.current?.click();
  const [libraryOpen, setLibraryOpen] = useState(false);

  /**
   * After inserting an image, ProseMirror leaves the selection AFTER the
   * node, which means `editor.isActive("image")` is false and the image
   * toolbar (align / size / style) stays hidden until the author clicks
   * the image. Select the just-inserted node so the toolbar pops up
   * immediately — matches what authors expect from any rich text editor.
   */
  const selectInsertedImage = (ed) => {
    if (!ed || ed.isDestroyed) return;
    ed.commands.command(({ tr, state, dispatch }) => {
      const pos = state.selection.from - 1;
      if (pos < 0) return false;
      const node = state.doc.nodeAt(pos);
      if (!node || node.type.name !== "image") return false;
      if (dispatch) dispatch(tr.setSelection(NodeSelection.create(state.doc, pos)));
      return true;
    });
  };

  const insertImageFromUrl = async (url) => {
    if (!url) return;
    const dims = await getImageDimensions(url);
    editor
      .chain()
      .focus()
      .setImage({
        src: url,
        alignment: "left",
        ...(dims ? { naturalWidth: dims.width, naturalHeight: dims.height } : {}),
      })
      .run();
    selectInsertedImage(editor);
    setLibraryOpen(false);
  };

  const insertYoutube = useCallback(() => {
    if (!editor) return;
    const url = window.prompt(
      "YouTube or Dailymotion URL (paste a watch link, share URL or dai.ly link)",
      "",
    );
    if (!url) return;
    const trimmed = url.trim();
    // Try Dailymotion first (dai.ly / dailymotion.com), then fall back to
    // YouTube — each command's own extension validates and extracts the id
    // internally; if both return false the URL wasn't a valid link for
    // either platform.
    const ok = isValidDailymotionUrl(trimmed)
      ? editor.chain().focus().setDailymotionVideo({ src: trimmed }).run()
      : editor.chain().focus().setYoutubeVideo({ src: trimmed }).run();
    if (!ok) {
      window.alert(
        "That doesn't look like a YouTube or Dailymotion link. Paste a normal youtube.com/watch?v=…, youtu.be/…, dailymotion.com/video/… or dai.ly/… URL.",
      );
    }
  }, [editor]);

  const insertEmoji = useCallback((emoji) => {
    if (!editor || !emoji) return;
    if (emoji === "▲_green") {
      editor.chain().focus().insertContent('<span style="color: #16A34A">▲</span>').run();
    } else if (emoji === "↔️_orange") {
      editor.chain().focus().insertContent('<span style="color: #F59E0B">↔</span>').run();
    } else if (emoji === "🆕_blue") {
      editor.chain().focus().insertContent('<span data-new-badge>🆕</span>').run();
    } else {
      editor.chain().focus().insertContent(emoji).run();
    }
  }, [editor]);

  const onImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    if (file.size > 8 * 1024 * 1024) {
      window.alert("Image is too large — please keep under 8 MB.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setImageUploading(true);
    try {
      const r = await api.post("/uploads", formData);
      const url = r.data?.url;
      if (url) {
        const dims = await getImageDimensions(url);
        editor
          .chain()
          .focus()
          .setImage({
            src: url,
            alignment: "left",
            ...(dims ? { naturalWidth: dims.width, naturalHeight: dims.height } : {}),
          })
          .run();
        selectInsertedImage(editor);
      }
    } catch (err) {
      window.alert(
        "Image upload failed: " + (err.response?.data?.detail || err.message),
      );
    } finally {
      setImageUploading(false);
      // Reset so re-uploading the same file fires onChange
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onPickVideo = () => videoInputRef.current?.click();

  const onVideoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    // Mirror the backend cap (25 MB) so we fail fast with a clear message.
    if (file.size > 25 * 1024 * 1024) {
      window.alert("Video is too large — please keep under 25 MB.");
      if (videoInputRef.current) videoInputRef.current.value = "";
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setVideoUploading(true);
    try {
      const r = await api.post("/uploads", formData);
      const url = r.data?.url;
      if (url) {
        editor.chain().focus().setVideo({ src: url }).run();
      }
    } catch (err) {
      window.alert(
        "Video upload failed: " + (err.response?.data?.detail || err.message),
      );
    } finally {
      setVideoUploading(false);
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  if (!editor) return null;

  const isImageSelected = editor.isActive("image");

  // The Size/Align/Style row above is shared by two different kinds of
  // "selected image": a plain standalone `image` node (driven through
  // `editor`/`updateAttributes("image", ...)` as before), or the image
  // inside an active image+text block (driven through
  // `imageTextActiveBlock.updateAttributes`, supplied via
  // `ImageTextToolbarContext`). These helpers pick whichever applies so
  // the row's buttons don't need to know which mode they're in.
  const imageTextActiveBlock = imageTextActive;
  const currentImageAlign = imageTextActiveBlock
    ? imageTextActiveBlock.attrs.align || "left"
    : editor.getAttributes("image")?.alignment || "center";
  const currentImageWidth = imageTextActiveBlock
    ? imageTextActiveBlock.attrs.width
    : editor.getAttributes("image")?.width;
  const currentImageRounded = imageTextActiveBlock
    ? imageTextActiveBlock.attrs.rounded !== false
    : editor.getAttributes("image")?.rounded !== false;
  const currentImageBordered = imageTextActiveBlock
    ? !!imageTextActiveBlock.attrs.bordered
    : !!editor.getAttributes("image")?.bordered;
  const onImageAlignClick = (dir) => {
    if (imageTextActiveBlock) imageTextActiveBlock.updateAttributes({ align: dir });
    else setImageAlign(dir);
  };
  const onImageWidthClick = (w) => {
    if (imageTextActiveBlock) imageTextActiveBlock.updateAttributes({ width: w });
    else setImageWidth(w);
  };
  const onToggleImageRoundedClick = () => {
    if (imageTextActiveBlock) {
      imageTextActiveBlock.updateAttributes({ rounded: !currentImageRounded });
    } else toggleImageRounded();
  };
  const onToggleImageBorderedClick = () => {
    if (imageTextActiveBlock) {
      imageTextActiveBlock.updateAttributes({ bordered: !currentImageBordered });
    } else toggleImageBorder();
  };

  const renderToolbar = (isTop) => {
    return (
      <div 
        className={`flex flex-wrap items-center gap-1 bg-[#F8FAFC] p-2 ${isTop ? "border-b border-black/10" : "border-t border-black/10"}`}
        data-testid={isTop ? "editor-toolbar-top" : "editor-toolbar-bottom"}
      >
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          icon={Bold}
          title="Bold (Ctrl+B)"
          testid="toolbar-bold"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          icon={Italic}
          title="Italic (Ctrl+I)"
          testid="toolbar-italic"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive("underline")}
          icon={Underline}
          title="Underline (Ctrl+U)"
          testid="toolbar-underline"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}
          icon={Strikethrough}
          title="Strikethrough"
          testid="toolbar-strike"
        />
        <Divider />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          icon={Heading2}
          title="Heading 2"
          testid="toolbar-h2"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
          icon={Heading3}
          title="Heading 3"
          testid="toolbar-h3"
        />
        <Divider />
        <div className="inline-flex items-center gap-1" data-testid="toolbar-text-size-group">
          <Type size={14} className="text-[var(--brand-body)]" title="Text size" />
          {TEXT_SIZE_BUTTONS.map((b) => (
            <button
              key={b.value || "default"}
              type="button"
              // Buttons preserve the editor selection automatically as long
              // as we stop the focus from jumping to them. preventDefault on
              // mouseDown keeps focus + selection inside the editor so the
              // mark applies to the highlighted text.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setTextSize(b.value)}
              data-testid={`toolbar-text-size-${b.value || "default"}`}
              title={b.title}
              className={[
                "rounded-sm px-1.5 py-1 font-display text-xs font-semibold transition-colors",
                (editor.getAttributes("textSize")?.size || "") === b.value
                  ? "bg-[#0A0F17] text-white"
                  : "border border-black/10 bg-white text-[var(--brand-ink)] hover:border-[#0A0F17]",
              ].join(" ")}
            >
              {b.label}
            </button>
          ))}
        </div>
        <Divider />
        {/* Text colour — native colour input is the most reliable picker
            across browsers and works fine with the editor selection so
            long as we stop focus jumping out. The colour persists as a
            <span style="color: …"> via the TextStyle + Color extensions. */}
        <div
          className="flex items-center gap-1.5"
          data-testid="toolbar-color-group"
        >
          <Palette size={14} className="text-[var(--brand-body)]" title="Text colour" />
          <div className="flex items-center gap-1">
            {PRESET_COLORS.map((c) => {
              const isActive = editor.getAttributes("textStyle")?.color === c.value;
              return (
                <button
                  key={c.value}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => editor.chain().focus().setColor(c.value).run()}
                  title={c.label}
                  data-testid={`toolbar-color-${c.label.toLowerCase().replace(" ", "-")}`}
                  className={[
                    "h-6 w-6 rounded-md border transition-all",
                    isActive 
                      ? "border-[#6CABDD] ring-2 ring-[#6CABDD]/40 scale-110" 
                      : "border-black/10 hover:border-black/30",
                    c.bgClass,
                  ].join(" ")}
                  aria-label={`Set color to ${c.label}`}
                />
              );
            })}
          </div>
          {editor.getAttributes("textStyle")?.color ? (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().unsetColor().run()}
              data-testid="toolbar-text-color-clear"
              title="Reset to default colour"
              className="rounded-sm border border-black/10 bg-white px-1.5 py-1 font-display text-xs font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17]"
            >
              clear
            </button>
          ) : null}
        </div>
        <Divider />
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          active={editor.isActive({ textAlign: "left" })}
          icon={AlignLeft}
          title="Align Left"
          testid="toolbar-align-left"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          active={editor.isActive({ textAlign: "center" })}
          icon={AlignCenter}
          title="Align Center"
          testid="toolbar-align-center"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          active={editor.isActive({ textAlign: "right" })}
          icon={AlignRight}
          title="Align Right"
          testid="toolbar-align-right"
        />
        <Divider />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          icon={List}
          title="Bullet list"
          testid="toolbar-bullet"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          icon={ListOrdered}
          title="Numbered list"
          testid="toolbar-ordered"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          icon={Quote}
          title="Blockquote"
          testid="toolbar-quote"
        />
        <Divider />
        <ToolbarButton
          onClick={setLink}
          active={editor.isActive("link")}
          icon={LinkIcon}
          title="Add / edit link"
          testid="toolbar-link"
        />
        <ToolbarButton
          onClick={onPickImage}
          icon={ImageIcon}
          title="Insert image (uploads + inserts at cursor)"
          testid="toolbar-image"
        />
        <ToolbarButton
          onClick={() => setLibraryOpen(true)}
          icon={FolderOpen}
          title="Insert from media library"
          testid="toolbar-image-from-library"
        />
        <ToolbarButton
          onClick={insertYoutube}
          icon={YoutubeIcon}
          title="Embed a YouTube or Dailymotion video"
          testid="toolbar-youtube"
        />
        <ToolbarButton
          onClick={onPickVideo}
          icon={videoUploading ? Loader2 : VideoIcon}
          title="Upload a video (MP4 / WebM / MOV, max 25 MB)"
          testid="toolbar-video"
          iconSpin={videoUploading}
        />
        <ToolbarButton
          onClick={openCarouselPicker}
          icon={CarouselIcon}
          title="Insert a photo carousel"
          testid="toolbar-carousel"
        />
        <ToolbarButton
          onClick={() => openEmbedDialog()}
          icon={EmbedIcon}
          title="Insert HTML embed (raw HTML, iframe, Facebook, Twitter/X, Instagram, TikTok…)"
          testid="toolbar-html-embed"
        />
        <ToolbarButton
          onClick={() => setSpotifyDialog({ url: "" })}
          icon={Music}
          title="Embed a Spotify playlist, track, or album"
          testid="toolbar-spotify"
        />
        <ToolbarButton
          onClick={openPollDialog}
          icon={BarChart3}
          title="Insert a voting poll"
          testid="toolbar-poll"
        />
        <ToolbarButton
          onClick={() => setTableDialog({ rows: 3, cols: 3, with_header: true })}
          icon={TableIcon}
          active={editor.isActive("table")}
          title="Insert a table"
          testid="toolbar-table"
        />
        <div className="relative">
          <ToolbarButton
            onClick={(e) => {
              setEmojiAnchor(e.currentTarget);
              setShowEmojiPicker((v) => !v);
            }}
            icon={Smile}
            active={showEmojiPicker}
            title="Insert an emoji"
            testid="toolbar-emoji"
          />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onImageFile}
          className="hidden"
          data-testid="toolbar-image-input"
        />
        <MediaPicker
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          onSelect={insertImageFromUrl}
          accept="image"
        />
        <input
          ref={videoInputRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          onChange={onVideoFile}
          className="hidden"
          data-testid="toolbar-video-input"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          icon={Minus}
          title="Section break"
          testid="toolbar-hr"
        />
        <Divider />
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          icon={Undo2}
          title="Undo (Ctrl+Z)"
          disabled={!editor.can().undo()}
          testid="toolbar-undo"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          icon={Redo2}
          title="Redo (Ctrl+Shift+Z)"
          disabled={!editor.can().redo()}
          testid="toolbar-redo"
        />
        <ToolbarButton
          onClick={() => setSpellCheck((v) => !v)}
          icon={SpellCheck}
          active={spellCheck}
          title={spellCheck ? "Spell-check on — click to turn off" : "Spell-check off — click to turn on"}
          testid="toolbar-spellcheck"
        />

        {/* Image alignment + size row — shown when either a plain image
            is selected, or an image+text block's image is "active"
            (clicked into — see `ImageTextToolbarContext`). `left`/
            `right` here are plain block-position alignment (no text-
            wrap/float), so switching between left/centre/right never
            changes the image's rendered size, only its horizontal
            position. Existing published posts that already used the
            old float-left/float-right CSS classes keep rendering
            exactly as before (those CSS rules are untouched) — this
            only changes what NEW images render as. Wrapped in a real
            (if layout-invisible, via `contents`) DOM element carrying
            `data-image-text-toolbar-guard` so the image+text block's
            "click outside closes it" listener knows clicks on these
            buttons aren't "outside" and shouldn't deactivate it mid-
            click. */}
        {isImageSelected || imageTextActiveBlock ? (
          <div className="contents" data-image-text-toolbar-guard>
            <Divider />
            <ToolbarButton
              onClick={() => onImageAlignClick("left")}
              active={currentImageAlign === "left"}
              icon={AlignLeft}
              title="Align image left"
              testid="toolbar-image-align-left"
            />
            <ToolbarButton
              onClick={() => onImageAlignClick("center")}
              active={currentImageAlign === "center"}
              icon={AlignCenter}
              title="Centre image"
              testid="toolbar-image-align-center"
            />
            <ToolbarButton
              onClick={() => onImageAlignClick("right")}
              active={currentImageAlign === "right"}
              icon={AlignRight}
              title="Align image right"
              testid="toolbar-image-align-right"
            />
            <Divider />
            <span className="ml-1 mr-1 label-kicker text-[var(--brand-body)]">
              Size
            </span>
            {[
              { w: 25, label: "S", title: "Small (25% width)" },
              { w: 50, label: "M", title: "Medium (50% width)" },
              { w: 75, label: "L", title: "Large (75% width)" },
              { w: 100, label: "XL", title: "Full width (100%)" },
            ].map((s) => (
              <SizeButton
                key={s.w}
                onClick={() => onImageWidthClick(s.w)}
                active={currentImageWidth === s.w}
                label={s.label}
                title={s.title}
                testid={`toolbar-size-${s.label.toLowerCase()}`}
              />
            ))}
            <SizeButton
              onClick={() => onImageWidthClick(null)}
              active={!currentImageWidth}
              label="Auto"
              title="Reset to default size"
              testid="toolbar-size-auto"
            />
            <Divider />
            <span className="ml-1 mr-1 label-kicker text-[var(--brand-body)]">
              Style
            </span>
            <ToolbarButton
              onClick={onToggleImageRoundedClick}
              active={currentImageRounded}
              icon={CornerUpRight}
              title="Rounded corners"
              testid="toolbar-image-rounded"
            />
            <ToolbarButton
              onClick={onToggleImageBorderedClick}
              active={currentImageBordered}
              icon={Square}
              title="Black border"
              testid="toolbar-image-bordered"
            />
          </div>
        ) : null}

        {/* Table tools — only shown when the cursor is inside a table. */}
        {editor.isActive("table") ? (
          <>
            <Divider />
            <span className="ml-1 mr-1 label-kicker text-[var(--brand-body)]">
              Table
            </span>
            <ToolbarButton
              onClick={() => editor.chain().focus().addRowAfter().run()}
              icon={Rows3}
              title="Add row below"
              testid="toolbar-table-add-row"
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              icon={Columns3}
              title="Add column right"
              testid="toolbar-table-add-col"
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeaderRow().run()}
              icon={Plus}
              title="Toggle header row"
              testid="toolbar-table-toggle-header"
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().deleteRow().run()}
              icon={Trash2}
              title="Delete current row"
              testid="toolbar-table-del-row"
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().deleteColumn().run()}
              icon={Trash2}
              title="Delete current column"
              testid="toolbar-table-del-col"
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().deleteTable().run()}
              icon={Trash2}
              title="Delete the whole table"
              testid="toolbar-table-delete"
            />
          </>
        ) : null}
      </div>
    );
  };

  return (
    <ImageTextToolbarContext.Provider value={{ active: imageTextActive, setActive: setImageTextActive }}>
    <div
      className="overflow-hidden rounded-md border border-black/15 bg-white relative"
      data-testid="rich-text-editor"
    >
      {renderToolbar(true)}

      <div className="relative">
        <EditorContent editor={editor} placeholder={placeholder} />

        {imageUploading ? (
          <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-white/70 backdrop-blur-xs font-display text-sm font-semibold text-[var(--brand-ink)]">
            <Loader2 className="h-6 w-6 animate-spin text-[#6CABDD] mb-2" />
            Uploading large image... please wait
          </div>
        ) : null}
      </div>

      {renderToolbar(false)}

      {showEmojiPicker ? (
        <EmojiPicker
          anchorEl={emojiAnchor}
          onPick={(e) => insertEmoji(e)}
          onClose={() => {
            setShowEmojiPicker(false);
            setEmojiAnchor(null);
          }}
        />
      ) : null}

      {pollDialog ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="poll-dialog-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPollDialog(null);
          }}
        >
          <div className="w-full max-w-md rounded-md border border-black/10 bg-white p-6 shadow-xl animate-scale-up text-left">
            <h3 className="font-display text-lg font-black text-[var(--brand-ink)] flex items-center gap-1.5">
              <span>📊</span> Insert a Voting Poll
            </h3>
            <p className="mt-1 label-kicker text-[var(--brand-body)]">
              Select any existing fan poll from your database, or click the link below to go to the Polls Manager to build a new one.
            </p>
            
            {allPolls.length === 0 ? (
              <p className="mt-4 text-sm text-[var(--brand-body)] italic border border-dashed rounded p-4 text-center">
                No active polls found. Please create one first.
              </p>
            ) : (
              <label className="block mt-4">
                <span className="label-kicker text-[var(--brand-body)]">Choose Poll:</span>
                <select
                  value={pollDialog.selected_id}
                  onChange={(e) => setPollDialog({ ...pollDialog, selected_id: e.target.value })}
                  data-testid="poll-dialog-select"
                  className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm text-[var(--brand-ink)] focus:border-[#6CABDD] focus:outline-none"
                >
                  {allPolls.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.question} {p.closed ? "(Closed)" : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="mt-4 flex items-center justify-between">
              <RouterLink
                to="/admin/polls"
                target="_blank"
                className="text-xs font-bold text-[#6CABDD] hover:underline"
              >
                Manage Polls Manager →
              </RouterLink>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPollDialog(null)}
                  className="rounded-md border border-black/15 bg-white px-3 py-1.5 font-display text-xs font-semibold text-[var(--brand-body)] hover:border-[#0A0F17]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (pollDialog.selected_id) {
                      editor.chain().focus().insertContent({ type: "poll", attrs: { id: pollDialog.selected_id } }).run();
                    }
                    setPollDialog(null);
                  }}
                  disabled={!pollDialog.selected_id}
                  className="rounded-md bg-[#0A0F17] px-4 py-1.5 font-display text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40"
                  data-testid="poll-dialog-save"
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {embedDialog ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="html-embed-dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEmbedDialog(null);
          }}
        >
          <div className="w-full max-w-2xl rounded-md border border-black/10 bg-white p-5 shadow-xl">
            <h3 className="font-display text-lg font-black text-[var(--brand-ink)]">
              {embedDialog.pos !== null ? "Edit HTML embed" : "Insert HTML embed"}
            </h3>
            <p className="mt-1 label-kicker text-[var(--brand-body)]">
              Paste a direct post URL (from Instagram, Twitter/X, TikTok, or YouTube) and we will auto-format it, OR paste raw HTML / an iframe embed code directly — it's kept exactly as pasted, no sanitising.
            </p>
            <textarea
              autoFocus
              rows={10}
              value={embedDialog.html}
              placeholder="Paste link (e.g. https://x.com/... or https://instagram.com/p/...) or raw HTML embed code here"
              onChange={(e) => setEmbedDialog({ ...embedDialog, html: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEmbedDialog(null);
              }}
              spellCheck={false}
              data-testid="html-embed-dialog-input"
              className="mt-3 w-full rounded-md border border-black/15 bg-[#F8FAFC] px-3 py-2 font-mono text-xs text-[var(--brand-ink)] focus:border-[#6CABDD] focus:outline-none"
            />
            <label className="block mt-3">
              <span className="label-kicker text-[var(--brand-body)]">Embed Width / Alignment</span>
              <select
                value={embedDialog.width || "full"}
                onChange={(e) => setEmbedDialog({ ...embedDialog, width: e.target.value })}
                data-testid="html-embed-dialog-width"
                className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm text-[var(--brand-ink)] focus:border-[#6CABDD] focus:outline-none"
              >
                <option value="full">Full Width (100%)</option>
                <option value="large">Large (Max 650px)</option>
                <option value="medium">Medium (Max 450px)</option>
                <option value="small">Small (Max 350px)</option>
              </select>
            </label>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-[var(--brand-body)]">
                Blank to {embedDialog.pos !== null ? "remove this embed" : "cancel"}.
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEmbedDialog(null)}
                  className="rounded-md border border-black/15 bg-white px-3 py-1.5 font-display text-xs font-semibold text-[var(--brand-body)] hover:border-[#0A0F17]"
                  data-testid="html-embed-dialog-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={applyEmbed}
                  className="rounded-md bg-[#0A0F17] px-4 py-1.5 font-display text-xs font-semibold text-white hover:opacity-90"
                  data-testid="html-embed-dialog-save"
                >
                  {embedDialog.pos !== null
                    ? (embedDialog.html.trim() ? "Save changes" : "Remove embed")
                    : "Insert"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {spotifyDialog ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="spotify-dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSpotifyDialog(null);
          }}
        >
          <div className="w-full max-w-lg rounded-md border border-black/10 bg-white p-6 shadow-xl animate-scale-up text-left">
            <h3 className="font-display text-lg font-black text-[var(--brand-ink)]">
              Embed Spotify Media
            </h3>
            <p className="mt-1 label-kicker text-[var(--brand-body)]">
              Paste any public Spotify link (playlist, track, album, artist, podcast show, or episode). It will automatically convert to a beautiful embedded Spotify player frame.
            </p>
            <input
              type="text"
              autoFocus
              value={spotifyDialog.url}
              placeholder="e.g., https://open.spotify.com/playlist/0F8EP9CUOEJDnIO95Q7kVg"
              onChange={(e) => setSpotifyDialog({ ...spotifyDialog, url: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Escape") setSpotifyDialog(null);
                if (e.key === "Enter") applySpotifyEmbed();
              }}
              spellCheck={false}
              data-testid="spotify-dialog-input"
              className="mt-3 w-full rounded-md border border-black/15 bg-[#F8FAFC] px-3 py-2 text-sm text-[var(--brand-ink)] focus:border-[#6CABDD] focus:outline-none"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSpotifyDialog(null)}
                className="rounded-md border border-black/15 bg-white px-3 py-1.5 font-display text-xs font-semibold text-[var(--brand-body)] hover:border-[#0A0F17]"
                data-testid="spotify-dialog-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applySpotifyEmbed}
                className="rounded-md bg-[#0A0F17] px-4 py-1.5 font-display text-xs font-semibold text-white hover:opacity-90"
                data-testid="spotify-dialog-save"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {linkDialog ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="link-dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) setLinkDialog(null);
          }}
        >
          <div className="w-full max-w-md rounded-md border border-black/10 bg-white p-5 shadow-xl">
            <h3 className="font-display text-lg font-black text-[var(--brand-ink)]">
              {linkDialog.mode === "image"
                ? (linkDialog.url ? "Edit image link" : "Link image to URL")
                : (editor.isActive("link") ? "Edit link" : "Add link")}
            </h3>
            <p className="mt-1 label-kicker text-[var(--brand-body)]">
              {linkDialog.mode === "image"
                ? "Where should the image go when clicked? Blank to remove."
                : "Leave URL blank to remove the link."}
            </p>
            <label className="mt-4 block label-kicker text-[var(--brand-body)]">
              URL
            </label>
            <input
              type="url"
              value={linkDialog.url}
              autoFocus
              placeholder="https://example.com"
              onChange={(e) =>
                setLinkDialog({ ...linkDialog, url: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyLink();
                }
                if (e.key === "Escape") setLinkDialog(null);
              }}
              data-testid="link-dialog-url"
              className="mt-1 w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-sm text-[var(--brand-ink)] focus:border-[#6CABDD] focus:outline-none"
            />
            <InternalDestPicker
              onPick={(url) =>
                setLinkDialog((d) => (d ? { ...d, url, newTab: false } : d))
              }
            />
            <label className="mt-4 flex items-center gap-2 text-sm text-[var(--brand-ink)]">
              <input
                type="checkbox"
                checked={linkDialog.newTab}
                onChange={(e) =>
                  setLinkDialog({ ...linkDialog, newTab: e.target.checked })
                }
                data-testid="link-dialog-newtab"
                className="h-4 w-4 cursor-pointer accent-[#6CABDD]"
              />
              Open in a new tab
            </label>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setLinkDialog(null)}
                data-testid="link-dialog-cancel"
                className="rounded-md border border-black/15 bg-white px-3 py-1.5 text-sm font-bold text-[var(--brand-ink)] hover:bg-black/5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyLink}
                data-testid="link-dialog-save"
                className="rounded-md bg-[#0A0F17] px-4 py-1.5 font-display text-sm font-black uppercase tracking-wider text-white hover:bg-black"
              >
                {linkDialog.url.trim() === "" && editor.isActive("link")
                  ? "Remove link"
                  : "Save link"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {tableDialog ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="table-dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) setTableDialog(null);
          }}
        >
          <div className="w-full max-w-sm rounded-md border border-black/10 bg-white p-5 shadow-xl">
            <h3 className="font-display text-lg font-black text-[var(--brand-ink)]">
              Insert a table
            </h3>
            <p className="mt-1 label-kicker text-[var(--brand-body)]">
              Pick how many rows and columns. You can add or remove rows/columns later from the toolbar.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <label className="block">
                <span className="label-kicker text-[var(--brand-body)]">Rows</span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={tableDialog.rows}
                  onChange={(e) =>
                    setTableDialog({
                      ...tableDialog,
                      rows: Math.max(1, Math.min(50, Number(e.target.value) || 1)),
                    })
                  }
                  data-testid="table-dialog-rows"
                  className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
                />
              </label>
              <label className="block">
                <span className="label-kicker text-[var(--brand-body)]">Columns</span>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={tableDialog.cols}
                  onChange={(e) =>
                    setTableDialog({
                      ...tableDialog,
                      cols: Math.max(1, Math.min(20, Number(e.target.value) || 1)),
                    })
                  }
                  data-testid="table-dialog-cols"
                  className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
                />
              </label>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!tableDialog.with_header}
                onChange={(e) =>
                  setTableDialog({ ...tableDialog, with_header: e.target.checked })
                }
                data-testid="table-dialog-header"
                className="h-4 w-4"
              />
              <span>Make the first row a header</span>
            </label>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setTableDialog(null)}
                className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .insertTable({
                      rows: tableDialog.rows,
                      cols: tableDialog.cols,
                      withHeaderRow: tableDialog.with_header,
                    })
                    .run();
                  setTableDialog(null);
                }}
                data-testid="table-dialog-insert"
                className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white"
              >
                <TableIcon size={14} /> Insert
              </button>
            </div>
          </div>
        </div>
      ) : null}


      {carouselPicker ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="carousel-picker"
          onClick={(e) => {
            if (e.target === e.currentTarget) setCarouselPicker(null);
          }}
        >
          <div className="w-full max-w-md rounded-md border border-black/10 bg-white p-5 shadow-xl">
            <h3 className="font-display text-lg font-black text-[var(--brand-ink)]">
              Insert a photo carousel
            </h3>
            <p className="mt-1 label-kicker text-[var(--brand-body)]">
              Pick one to drop a <code className="font-mono">[[carousel:slug]]</code> shortcode at the cursor.
            </p>

            {carouselPicker.items === null ? (
              <div className="mt-5 flex items-center justify-center gap-2 py-6 text-sm text-[var(--brand-body)]">
                <Loader2 size={14} className="animate-spin" /> Loading carousels…
              </div>
            ) : carouselPicker.items.length === 0 ? (
              <div className="mt-5 rounded-md border border-dashed border-black/15 p-5 text-center text-sm text-[var(--brand-body)]">
                <p>No carousels yet.</p>
                <a
                  href="/admin/carousels"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block underline underline-offset-4 hover:text-[var(--brand-ink)]"
                  data-testid="carousel-picker-create-link"
                >
                  Create one in Admin → Photo carousels →
                </a>
              </div>
            ) : (
              <ul className="mt-4 max-h-72 overflow-y-auto rounded-md border border-black/10">
                {carouselPicker.items.map((c) => (
                  <li key={c.slug}>
                    <button
                      type="button"
                      onClick={() => insertCarousel(c.slug)}
                      data-testid={`carousel-picker-item-${c.slug}`}
                      className="flex w-full items-center justify-between gap-3 border-b border-black/5 px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-black/5"
                    >
                      <span className="min-w-0">
                        <span className="block font-display text-sm font-bold text-[var(--brand-ink)] truncate">
                          {c.name || c.slug}
                        </span>
                        <span className="label-kicker text-[var(--brand-body)]">
                          {(c.images || []).length} photo{(c.images || []).length === 1 ? "" : "s"} · /gallery/{c.slug}
                        </span>
                      </span>
                      <span className="shrink-0 label-kicker text-brand-accent">Insert →</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {carouselPicker.error ? (
              <p className="mt-3 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm">
                {carouselPicker.error}
              </p>
            ) : null}

            <div className="mt-5 flex items-center justify-end">
              <button
                type="button"
                onClick={() => setCarouselPicker(null)}
                data-testid="carousel-picker-cancel"
                className="rounded-md border border-black/15 bg-white px-3 py-1.5 text-sm font-bold text-[var(--brand-ink)] hover:bg-black/5"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
    </ImageTextToolbarContext.Provider>
  );
}

function ToolbarButton({ onClick, active, icon: Icon, title, disabled, testid, iconSpin, buttonRef }) {
  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-sm transition-colors disabled:opacity-30 ${
        active
          ? "bg-[#0A0F17] text-white"
          : "text-[var(--brand-ink)] hover:bg-black/5"
      }`}
    >
      <Icon size={14} strokeWidth={2.5} className={iconSpin ? "animate-spin" : ""} />
    </button>
  );
}

function SizeButton({ onClick, active, label, title, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`inline-flex h-8 min-w-[2.25rem] items-center justify-center rounded-sm px-2 font-display text-xs font-bold tracking-wider transition-colors ${
        active
          ? "bg-[#0A0F17] text-white"
          : "text-[var(--brand-ink)] hover:bg-black/5"
      }`}
    >
      {label}
    </button>
  );
}

function InternalDestPicker({ onPick }) {
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const needle = q.trim();
    if (!needle) {
      setResults([]);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    const timerId = setTimeout(() => {
      api.get(`/admin/search-destinations?q=${encodeURIComponent(needle)}`)
        .then((r) => {
          if (alive) setResults(r.data || []);
        })
        .catch(() => {
          if (alive) setResults([]);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 200);
    return () => {
      alive = false;
      clearTimeout(timerId);
    };
  }, [q]);

  const showList = q.trim().length > 0;

  return (
    <div className="mt-3" data-testid="link-dialog-internal-picker">
      <label className="block label-kicker text-[var(--brand-body)]">
        Or pick an internal page / post
      </label>
      <div className="relative mt-1">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Type to search pages &amp; posts…"
          data-testid="link-dialog-internal-search"
          className="w-full rounded-md border border-black/15 bg-white pl-3 pr-10 py-2 text-sm text-[var(--brand-ink)] focus:border-[#6CABDD] focus:outline-none"
        />
        {loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-[#6CABDD]" />
          </span>
        )}
      </div>

      {showList ? (
        <ul
          className="mt-2 max-h-48 overflow-y-auto rounded-md border border-black/10 bg-[#F8FAFC]"
          data-testid="link-dialog-internal-list"
        >
          {results.map((d) => (
            <li key={`${d.type}-${d.url}`}>
              <button
                type="button"
                onClick={() => onPick(d.url)}
                data-testid={`link-dest-${d.url}`}
                className="flex w-full items-baseline justify-between gap-3 border-b border-black/5 px-3 py-2 text-left text-sm hover:bg-white"
              >
                <span className="truncate font-display font-bold text-[var(--brand-ink)]">
                  {d.label}
                </span>
                <span className="shrink-0 label-kicker text-[var(--brand-body)]">
                  {d.hint}
                </span>
              </button>
            </li>
          ))}
          {results.length === 0 && !loading ? (
            <li className="px-3 py-2 label-kicker text-[var(--brand-body)]">
              No matches for &ldquo;{q}&rdquo;.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

function Divider() {
  return <span className="mx-1 h-5 w-px bg-black/10" aria-hidden />;
}

function EmojiPicker({ anchorEl, onPick, onClose }) {
  const [activeId, setActiveId] = React.useState(EMOJI_GROUPS[0].id);
  // Position is computed from the toolbar button's bounding rect so the
  // popover sits below + flush-right of the icon AND escapes the editor's
  // `overflow-hidden` wrapper (otherwise the popover gets clipped to a
  // narrow vertical strip). Re-measured on resize / scroll.
  const computePos = React.useCallback(() => {
    const btn = anchorEl;
    if (!btn) return { top: 0, left: 0 };
    const r = btn.getBoundingClientRect();
    const W = 288; // matches w-72
    const top = r.bottom + 8;
    // Anchor right edge of popover ~12px in from the button's right edge,
    // and clamp so it never overflows the viewport on small screens.
    const left = Math.max(
      8,
      Math.min(r.right - W + 12, window.innerWidth - W - 8),
    );
    return { top, left };
  }, [anchorEl]);
  const [pos, setPos] = React.useState(computePos);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const update = () => setPos(computePos());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [computePos]);

  // Close on outside click / escape so the popover doesn't sit on top of
  // the editor obscuring what the user is typing.
  React.useEffect(() => {
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorEl]);

  const active = EMOJI_GROUPS.find((g) => g.id === activeId) || EMOJI_GROUPS[0];

  return createPortal(
    <div
      ref={ref}
      data-testid="emoji-picker"
      style={{ position: "fixed", top: pos.top, left: pos.left, width: 288 }}
      className="z-[100] overflow-hidden rounded-md border border-black/10 bg-white shadow-lg"
    >
      <div className="flex items-center gap-0.5 border-b border-black/10 bg-[#F8FAFC] p-1">
        {EMOJI_GROUPS.map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => setActiveId(g.id)}
            title={g.label}
            data-testid={`emoji-tab-${g.id}`}
            className={`inline-flex h-7 w-7 items-center justify-center rounded-sm text-base transition-colors ${
              g.id === activeId ? "bg-white shadow-sm" : "hover:bg-white/60"
            }`}
          >
            {g.icon}
          </button>
        ))}
      </div>
      <div
        className="grid max-h-56 grid-cols-8 gap-0.5 overflow-y-auto p-1.5"
        data-testid={`emoji-grid-${active.id}`}
      >
        {active.items.map((e, i) => {
          const isSpecialGreen = e === "▲_green";
          const isSpecialOrange = e === "↔️_orange";
          const isSpecialBlueNew = e === "🆕_blue";
          return (
            <button
              key={`${e}-${i}`}
              type="button"
              onClick={() => onPick(e)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-lg leading-none transition-colors hover:bg-black/5"
              aria-label={isSpecialGreen ? "Insert green up triangle" : isSpecialOrange ? "Insert orange double arrow" : isSpecialBlueNew ? "Insert blue new badge" : `Insert ${e}`}
            >
              {isSpecialGreen ? (
                <span style={{ color: "#16A34A" }}>▲</span>
              ) : isSpecialOrange ? (
                <span style={{ color: "#F59E0B" }}>↔</span>
              ) : isSpecialBlueNew ? (
                <span style={{ backgroundColor: "#002D75", color: "#FFFFFF", padding: "1px 4px", borderRadius: "3px", fontSize: "8px", fontWeight: "900" }}>🆕</span>
              ) : (
                e
              )}
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}
